// frontend/src/screens/checkout/CheckoutScreen.js

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import clsx from "clsx";
import {
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  Loader2,
  PackageCheck,
  PencilLine,
  RefreshCw,
  ShoppingBag,
  Tag,
  Truck,
  X,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import Meta from "../../components/Meta";

import {
  getCart,
  getCheckout,
  getCheckoutQuote,
  applyPromoCode,
  removePromoCode,
  resetCartError,
  resetCartMeta,
  saveBillingOptions,
  saveCheckoutAcknowledgements,
  saveCheckoutNotes,
  saveCustomerDetails,
  saveFulfillmentMethod,
  savePaymentMethod,
  savePickupDetails,
  saveShippingAddress,
  validateCart,
} from "../../actions/cartActions";

import {
  createOrder,
  resetOrderCreate,
  resetOrderError,
  resetOrderMeta,
} from "../../actions/orderActions";
import { updateDefaultShippingAddress } from "../../actions/userActions";

import {
  clearCurrentPayment,
  createPaymentForExistingOrder,
  getPaymentConfig,
  resetPaymentError,
  resetPaymentMeta,
} from "../../actions/paymentActions";

import {
  PAYMENT_PROVIDER_LABELS,
  PAYMENT_PROVIDERS,
} from "../../constants/paymentConstants";

import {
  selectCreatedOrder,
  selectCreatedOrderNumber,
  selectOrderCreateState,
} from "../../selectors/orderSelectors";

import {
  selectAnyPaymentBusy,
  selectPaymentConfig,
} from "../../selectors/paymentSelectors";
import { getApiErrorMessage } from "../../utils/errorMessages";

import "./CheckoutScreen.css";

const FALLBACK_IMAGE = "https://placehold.co/640x760?text=Academy+Costumes+London";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

function useDebounce(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

function safeText(value = "") {
  return String(value ?? "").trim();
}

function normalizeLower(value = "") {
  return safeText(value).toLowerCase();
}

function normalizeUpper(value = "") {
  return safeText(value).toUpperCase();
}

function roundMoney(value = 0) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return 0;
  return Math.round(number * 100) / 100;
}

function formatMoney(value = 0, currency = "GBP") {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: normalizeUpper(currency) || "GBP",
    }).format(Number(value || 0));
  } catch {
    return `£${Number(value || 0).toFixed(2)}`;
  }
}

function normalizeImage(src = "") {
  return safeText(src) || FALLBACK_IMAGE;
}

function customerSafeText(value = "", fallback = "") {
  const text = safeText(value);
  const lower = text.toLowerCase();

  if (
    lower.includes("reserved") ||
    lower.includes("temporarily reserved") ||
    lower.includes("allocated") ||
    lower.includes("held")
  ) {
    return fallback;
  }

  return text || fallback;
}

function normalizeErrorMessage(
  error,
  fallback = "We could not complete this checkout step right now. Please try again later.",
) {
  if (typeof error === "string") return customerSafeText(error, fallback);

  const message =
    error?.payload?.message ||
    error?.data?.message ||
    getApiErrorMessage(error, { fallback });

  return customerSafeText(message, fallback);
}

function getReactEnvValue(key = "") {
  if (typeof process !== "undefined" && process.env) {
    return safeText(process.env[key]);
  }

  return "";
}

function emptyAddress() {
  return {
    fullName: "",
    company: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "GB",
    phone: "",
  };
}

function normalizeAddress(address = {}) {
  return {
    ...emptyAddress(),
    ...address,
    fullName: safeText(address?.fullName),
    company: safeText(address?.company),
    line1: safeText(address?.line1),
    line2: safeText(address?.line2),
    city: safeText(address?.city),
    state: safeText(address?.state),
    postalCode: safeText(address?.postalCode),
    country: normalizeUpper(address?.country || "GB"),
    phone: safeText(address?.phone),
  };
}

function normalizeCustomer(customer = {}, fallbackUser = {}) {
  const fallbackName =
    safeText(fallbackUser?.name) ||
    [safeText(fallbackUser?.firstName), safeText(fallbackUser?.lastName)]
      .filter(Boolean)
      .join(" ");

  return {
    name: safeText(customer?.name || fallbackName),
    email: normalizeLower(customer?.email || fallbackUser?.email),
    phone: safeText(customer?.phone || fallbackUser?.phone),
  };
}

function normalizePickupDetails(pickup = {}) {
  return {
    appointmentOnly:
      typeof pickup?.appointmentOnly === "boolean"
        ? pickup.appointmentOnly
        : true,
    date: safeText(pickup?.date),
    dayLabel: safeText(pickup?.dayLabel),
    timeSlot: safeText(pickup?.timeSlot),
    reference: safeText(pickup?.reference),
    instructions: safeText(pickup?.instructions),
  };
}

function addressComplete(address = {}) {
  return Boolean(
    safeText(address?.fullName) &&
    safeText(address?.line1) &&
    safeText(address?.city) &&
    safeText(address?.postalCode) &&
    safeText(address?.country),
  );
}

function hasUsefulAddress(address = {}) {
  return Boolean(
    safeText(address?.line1) &&
    safeText(address?.city) &&
    safeText(address?.postalCode) &&
    safeText(address?.country),
  );
}

function getSavedShippingAddress(user = {}) {
  return normalizeAddress(
    user?.defaultShippingAddress || user?.checkoutDefaults?.shippingAddress,
  );
}

function pickupComplete(pickup = {}) {
  return Boolean(safeText(pickup?.date) && safeText(pickup?.timeSlot));
}

function formatAddressSummary(address = {}) {
  const normalized = normalizeAddress(address);
  const summary = [
    normalized.line1,
    normalized.line2,
    normalized.city,
    normalized.postalCode,
    normalized.country,
  ]
    .map(safeText)
    .filter(Boolean)
    .join(", ");

  return summary || "Not added yet";
}

function formatPickupSummary(pickup = {}) {
  const summary = [pickup?.date, pickup?.timeSlot, pickup?.instructions]
    .map(safeText)
    .filter(Boolean)
    .join(", ");

  return summary || "Pickup details not added yet";
}

function formatContactSummary(customer = {}) {
  const summary = [customer?.name, customer?.email, customer?.phone]
    .map(safeText)
    .filter(Boolean)
    .join(" · ");

  return summary || "Contact details not added yet";
}

function formatFulfillmentSummary({
  fulfillmentMethod,
  shippingAddress,
  pickupDetails,
} = {}) {
  if (fulfillmentMethod === "pickup") {
    return formatPickupSummary(pickupDetails);
  }

  return formatAddressSummary(shippingAddress);
}

function formatBillingSummary({ billing, shippingAddress, fulfillmentMethod }) {
  if (billing?.useShippingAsBilling) {
    return fulfillmentMethod === "pickup"
      ? "Same as pickup contact details"
      : `Same as delivery address: ${formatAddressSummary(shippingAddress)}`;
  }

  return formatAddressSummary(billing?.address);
}

function getLineDescriptor(item = {}) {
  return [
    item?.variantName,
    item?.unitLabel ? `Per ${item.unitLabel}` : "",
    item?.color,
    item?.size,
    item?.material,
    item?.length,
  ]
    .map(safeText)
    .filter(Boolean)
    .join(" · ");
}

function isTextileLine(item = {}) {
  const returnPolicyType = normalizeUpper(item?.returnPolicyType);
  const productType = normalizeLower(item?.productType);
  const itemType = normalizeLower(item?.itemType);
  const shopKey = normalizeLower(item?.shopKey);
  const unitLabel = normalizeLower(item?.unitLabel);
  const variantType = normalizeLower(item?.variantType);

  return (
    returnPolicyType.includes("TEXTILE") ||
    returnPolicyType.includes("CUT") ||
    productType.includes("textile") ||
    itemType.includes("textile") ||
    shopKey.includes("textile") ||
    unitLabel.includes("metre") ||
    unitLabel.includes("meter") ||
    variantType.includes("metre") ||
    variantType.includes("meter") ||
    variantType.includes("cut")
  );
}

function hasTextilePolicyLines(cart = {}) {
  return (cart?.items || []).some(isTextileLine);
}

function getOrderFromAnyPayload(...sources) {
  for (const source of sources) {
    if (source?.order) return source.order;
    if (source?.payload?.order) return source.payload.order;
    if (source?.payload?.data?.order) return source.payload.data.order;
    if (source?.data?.order) return source.data.order;
  }

  return null;
}

function getOrderNumberFromAny(...sources) {
  for (const source of sources) {
    const value = safeText(
      source?.orderNumber ||
        source?.order?.orderNumber ||
        source?.payload?.orderNumber ||
        source?.payload?.order?.orderNumber ||
        source?.payload?.data?.orderNumber ||
        source?.payload?.data?.order?.orderNumber ||
        source?.data?.orderNumber ||
        source?.data?.order?.orderNumber,
    );

    if (value) return value;
  }

  return "";
}

function getApprovalUrlFromLinks(links) {
  if (!Array.isArray(links)) return "";

  const approvalLink = links.find((link) => {
    const rel = normalizeLower(link?.rel);

    return (
      rel === "approve" ||
      rel === "approval_url" ||
      rel === "payer-action" ||
      rel === "payer_action" ||
      rel.includes("approve")
    );
  });

  return safeText(approvalLink?.href || approvalLink?.url);
}

function getApprovalUrlFromAny(...sources) {
  for (const source of sources) {
    const value = safeText(
      source?.checkoutUrl ||
        source?.approvalUrl ||
        source?.approveUrl ||
        source?.payerActionUrl ||
        source?.payment?.checkoutUrl ||
        source?.payment?.approvalUrl ||
        source?.payment?.approveUrl ||
        source?.payment?.payerActionUrl ||
        source?.nextAction?.url ||
        source?.payload?.checkoutUrl ||
        source?.payload?.approvalUrl ||
        source?.payload?.approveUrl ||
        source?.payload?.payerActionUrl ||
        source?.payload?.payment?.checkoutUrl ||
        source?.payload?.payment?.approvalUrl ||
        source?.payload?.payment?.approveUrl ||
        source?.payload?.payment?.payerActionUrl ||
        source?.payload?.nextAction?.url ||
        source?.payload?.data?.checkoutUrl ||
        source?.payload?.data?.approvalUrl ||
        source?.payload?.data?.approveUrl ||
        source?.payload?.data?.payerActionUrl ||
        source?.payload?.data?.payment?.checkoutUrl ||
        source?.payload?.data?.payment?.approvalUrl ||
        source?.payload?.data?.payment?.approveUrl ||
        source?.payload?.data?.payment?.payerActionUrl ||
        source?.payload?.data?.nextAction?.url ||
        source?.data?.checkoutUrl ||
        source?.data?.approvalUrl ||
        source?.data?.approveUrl ||
        source?.data?.payerActionUrl ||
        source?.data?.payment?.checkoutUrl ||
        source?.data?.payment?.approvalUrl ||
        source?.data?.payment?.approveUrl ||
        source?.data?.payment?.payerActionUrl ||
        source?.data?.nextAction?.url,
    );

    if (value) return value;

    const linkValue =
      getApprovalUrlFromLinks(source?.links) ||
      getApprovalUrlFromLinks(source?.payment?.links) ||
      getApprovalUrlFromLinks(source?.payload?.links) ||
      getApprovalUrlFromLinks(source?.payload?.payment?.links) ||
      getApprovalUrlFromLinks(source?.payload?.data?.links) ||
      getApprovalUrlFromLinks(source?.payload?.data?.payment?.links) ||
      getApprovalUrlFromLinks(source?.data?.links) ||
      getApprovalUrlFromLinks(source?.data?.payment?.links);

    if (linkValue) return linkValue;
  }

  return "";
}

function getQuoteTotals({ cart, quote }) {
  const subtotal = roundMoney(quote?.subtotal ?? cart?.subtotal ?? 0);
  const shippingTotal = roundMoney(quote?.shippingTotal ?? 0);
  const discountTotal = roundMoney(
    quote?.discountTotal ?? cart?.discountTotal ?? 0,
  );
  const taxTotal = roundMoney(quote?.taxTotal ?? 0);
  const grandTotal = roundMoney(
    quote?.grandTotal ?? subtotal + shippingTotal + taxTotal - discountTotal,
  );

  return {
    subtotal,
    shippingTotal,
    discountTotal,
    taxTotal,
    grandTotal,
  };
}

function getErrorIssues(error = {}) {
  const raw = [
    ...(Array.isArray(error?.issues) ? error.issues : []),
    ...(Array.isArray(error?.failures) ? error.failures : []),
    ...(Array.isArray(error?.inventoryFailures) ? error.inventoryFailures : []),
    ...(Array.isArray(error?.payload?.issues) ? error.payload.issues : []),
    ...(Array.isArray(error?.payload?.failures) ? error.payload.failures : []),
    ...(Array.isArray(error?.payload?.inventoryFailures)
      ? error.payload.inventoryFailures
      : []),
    ...(Array.isArray(error?.payload?.data?.issues)
      ? error.payload.data.issues
      : []),
    ...(Array.isArray(error?.data?.issues) ? error.data.issues : []),
  ];

  return raw.map((issue) => ({
    code: normalizeUpper(issue?.code),
    productName: safeText(issue?.productName || issue?.name),
    message: customerSafeText(
      issue?.customerMessage ||
        issue?.message ||
        issue?.statusReason ||
        issue?.stockStatusReason ||
        issue?.unavailableReason ||
        issue?.disabledReason,
      "Please review this item before continuing.",
    ),
  }));
}

export function getPaymentOptions(config = {}) {
  const providers = config?.providers || {};

  const enabledProviders = Array.isArray(config?.enabledProviders)
    ? config.enabledProviders.map(normalizeLower).filter(Boolean)
    : [];

  const stripeConfig = providers.stripe || config.stripe || {};
  const paypalConfig = providers.paypal || config.paypal || {};
  const sumupConfig = providers.sumup || config.sumup || {};

  const stripeKey = safeText(
    config?.stripePublicKey ||
      config?.stripePublishableKey ||
      stripeConfig?.publicKey ||
      stripeConfig?.publishableKey ||
      getReactEnvValue("REACT_APP_STRIPE_PUBLIC_KEY"),
  );

  const paypalClientId = safeText(
    config?.paypalClientId || paypalConfig?.clientId,
  );

  const hasExplicitProviderList = enabledProviders.length > 0;

  const stripeEnabled = hasExplicitProviderList
    ? enabledProviders.includes(PAYMENT_PROVIDERS.STRIPE)
    : Boolean(config?.stripeEnabled || stripeConfig?.enabled || stripeKey);

  const paypalEnabled =
    enabledProviders.includes(PAYMENT_PROVIDERS.PAYPAL) ||
    config?.paypalEnabled === true ||
    paypalConfig?.enabled === true ||
    Boolean(paypalClientId && paypalConfig?.enabled !== false);

  const sumupEnabled =
    enabledProviders.includes(PAYMENT_PROVIDERS.SUMUP) ||
    config?.sumupEnabled === true ||
    sumupConfig?.enabled === true;

  return [
    stripeEnabled
      ? {
          provider: PAYMENT_PROVIDERS.STRIPE,
          label:
            PAYMENT_PROVIDER_LABELS?.[PAYMENT_PROVIDERS.STRIPE] ||
            "Credit/Debit Card",
        }
      : null,

    paypalEnabled
      ? {
          provider: PAYMENT_PROVIDERS.PAYPAL,
          label:
            PAYMENT_PROVIDER_LABELS?.[PAYMENT_PROVIDERS.PAYPAL] || "PayPal",
        }
      : null,

    sumupEnabled
      ? {
          provider: PAYMENT_PROVIDERS.SUMUP,
          label:
            PAYMENT_PROVIDER_LABELS?.[PAYMENT_PROVIDERS.SUMUP] || "SumUp",
        }
      : null,
  ].filter(Boolean);
}

function getPaymentProviderName(provider = "") {
  if (provider === PAYMENT_PROVIDERS.STRIPE) return "Card";
  if (provider === PAYMENT_PROVIDERS.PAYPAL) return "PayPal";
  if (provider === PAYMENT_PROVIDERS.SUMUP) return "SumUp";

  return "Payment";
}

function buildAcknowledgements({
  needsTextileAcknowledgement,
  textileNoReturnAccepted,
}) {
  return {
    textileNoReturnAccepted: needsTextileAcknowledgement
      ? Boolean(textileNoReturnAccepted)
      : false,
    standardReturnsAccepted: true,
  };
}

/* -------------------------------------------------------------------------- */
/*                                UI Pieces                                   */
/* -------------------------------------------------------------------------- */

function CheckoutStep({ title, children }) {
  return (
    <section className="checkout-hof__step">
      <div className="checkout-hof__step-marker">
        <span />
      </div>

      <div className="checkout-hof__step-content">
        <h2>{title}</h2>
        {children}
      </div>
    </section>
  );
}

function SummaryLine({ label, value, strong = false }) {
  return (
    <div className={clsx("checkout-hof__summary-line", strong && "is-strong")}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ReviewPanel({ label, value }) {
  return (
    <div className="checkout-hof__review-panel">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ShippingReview({
  customer,
  fulfillmentMethod,
  shippingAddress,
  pickupDetails,
  billing,
}) {
  return (
    <div className="checkout-hof__review-grid">
      <ReviewPanel label="Contact" value={formatContactSummary(customer)} />
      <ReviewPanel
        label={fulfillmentMethod === "pickup" ? "Pickup" : "Delivery"}
        value={formatFulfillmentSummary({
          fulfillmentMethod,
          shippingAddress,
          pickupDetails,
        })}
      />
      <ReviewPanel
        label="Billing"
        value={formatBillingSummary({
          billing,
          shippingAddress,
          fulfillmentMethod,
        })}
      />
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  required = false,
  type = "text",
  autoComplete,
  placeholder = "",
}) {
  return (
    <label className="checkout-hof__field">
      <span>
        {label}
        {required ? <em>*</em> : null}
      </span>

      <input
        type={type}
        value={value || ""}
        autoComplete={autoComplete}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function AddressFields({ address, onChange, prefix = "shipping" }) {
  const update = useCallback(
    (key, value) => {
      onChange({
        ...address,
        [key]: value,
      });
    },
    [address, onChange],
  );

  return (
    <div className="checkout-hof__field-grid">
      <TextField
        label="Full name"
        required
        value={address.fullName}
        autoComplete={`${prefix} name`}
        onChange={(value) => update("fullName", value)}
      />

      <TextField
        label="Phone"
        value={address.phone}
        autoComplete={`${prefix} tel`}
        onChange={(value) => update("phone", value)}
      />

      <TextField
        label="Address line 1"
        required
        value={address.line1}
        autoComplete={`${prefix} address-line1`}
        onChange={(value) => update("line1", value)}
      />

      <TextField
        label="Address line 2"
        value={address.line2}
        autoComplete={`${prefix} address-line2`}
        onChange={(value) => update("line2", value)}
      />

      <TextField
        label="City"
        required
        value={address.city}
        autoComplete={`${prefix} address-level2`}
        onChange={(value) => update("city", value)}
      />

      <TextField
        label="Postcode"
        required
        value={address.postalCode}
        autoComplete={`${prefix} postal-code`}
        onChange={(value) => update("postalCode", value)}
      />

      <TextField
        label="Country"
        required
        value={address.country}
        autoComplete={`${prefix} country`}
        onChange={(value) => update("country", normalizeUpper(value))}
      />
    </div>
  );
}

function IssuesPanel({ issues, onRefresh, refreshing }) {
  if (!issues.length) return null;

  return (
    <section className="checkout-hof__issues">
      <div>
        <AlertTriangle size={18} />
        <strong>Basket needs attention</strong>
      </div>

      {issues.map((issue, index) => (
        <p key={`${issue.code}-${index}`}>
          {issue.productName ? `${issue.productName}: ` : ""}
          {issue.message}
        </p>
      ))}

      <button type="button" onClick={onRefresh} disabled={refreshing}>
        {refreshing ? (
          <Loader2 size={15} className="checkout-hof__spin" />
        ) : (
          <RefreshCw size={15} />
        )}
        Refresh basket
      </button>
    </section>
  );
}

function PaymentMethodPanel({
  provider,
  selected,
  disabled,
  payDisabled,
  totals,
  currency,
  itemCount,
  onSelect,
  onPay,
  processing,
}) {
  const isCard = provider === PAYMENT_PROVIDERS.STRIPE;
  const isPayPal = provider === PAYMENT_PROVIDERS.PAYPAL;
  const isSumUp = provider === PAYMENT_PROVIDERS.SUMUP;
  const providerName = getPaymentProviderName(provider);

  return (
    <section
      className={clsx(
        "checkout-hof__payment-method",
        selected && "is-selected",
        isPayPal && "is-paypal",
        isSumUp && "is-sumup",
      )}
    >
      <button
        type="button"
        className="checkout-hof__payment-head"
        onClick={() => onSelect(provider)}
        disabled={disabled}
      >
        <span className="checkout-hof__radio" />
        <strong>{isCard ? "Credit/Debit Card" : providerName}</strong>

        {isCard ? (
          <span className="checkout-hof__card-icon" aria-hidden="true" />
        ) : isPayPal ? (
          <span className="checkout-hof__paypal-mark">PayPal</span>
        ) : (
          <span className="checkout-hof__sumup-mark">SumUp</span>
        )}
      </button>

      {selected && isCard ? (
        <div className="checkout-hof__payment-body">
          <h3>Secure card checkout</h3>

          <p className="checkout-hof__payment-warning">
            You will be sent to Stripe to complete card payment securely.
          </p>

          <div className="checkout-hof__payment-totals">
            <SummaryLine
              label={`${itemCount} ${itemCount === 1 ? "item" : "items"}`}
              value={formatMoney(totals.subtotal, currency)}
            />
            <SummaryLine
              label="Delivery"
              value={formatMoney(totals.shippingTotal, currency)}
            />
            {totals.discountTotal > 0 ? (
              <SummaryLine
                label="Promo code"
                value={`-${formatMoney(totals.discountTotal, currency)}`}
              />
            ) : null}
            <SummaryLine
              label="Total"
              value={formatMoney(totals.grandTotal, currency)}
              strong
            />
          </div>

          <p className="checkout-hof__terms">
            By placing your order you agree to the website{" "}
            <Link to="/terms-and-conditions">terms and conditions</Link> and you
            can find out more about how we process your data{" "}
            <Link to="/privacy-policy">here</Link>.
          </p>

          <button
            type="button"
            className="checkout-hof__pay-button"
            disabled={payDisabled || processing}
            onClick={onPay}
          >
            {processing ? (
              <>
                <Loader2 size={16} className="checkout-hof__spin" />
                Processing…
              </>
            ) : (
              "Continue to Card Payment"
            )}
          </button>
        </div>
      ) : null}

      {selected && isPayPal ? (
        <div className="checkout-hof__payment-body">
          <div className="checkout-hof__payment-totals">
            <SummaryLine
              label={`${itemCount} ${itemCount === 1 ? "item" : "items"}`}
              value={formatMoney(totals.subtotal, currency)}
            />
            <SummaryLine
              label="Delivery"
              value={formatMoney(totals.shippingTotal, currency)}
            />
            {totals.discountTotal > 0 ? (
              <SummaryLine
                label="Promo code"
                value={`-${formatMoney(totals.discountTotal, currency)}`}
              />
            ) : null}
            <SummaryLine
              label="Total"
              value={formatMoney(totals.grandTotal, currency)}
              strong
            />
          </div>

          <p className="checkout-hof__terms">
            By placing your order you agree to the website{" "}
            <Link to="/terms-and-conditions">terms and conditions</Link> and you
            can find out more about how we process your data{" "}
            <Link to="/privacy-policy">here</Link>.
          </p>

          <button
            type="button"
            className="checkout-hof__paypal-button"
            disabled={payDisabled || processing}
            onClick={onPay}
          >
            {processing ? (
              <>
                <Loader2 size={16} className="checkout-hof__spin" />
                Connecting…
              </>
            ) : (
              <>
                Pay with <strong>PayPal</strong>
              </>
            )}
          </button>
        </div>
      ) : null}

      {selected && isSumUp ? (
        <div className="checkout-hof__payment-body">
          <h3>Secure SumUp checkout</h3>

          <p className="checkout-hof__payment-warning">
            You will be sent to SumUp to complete payment securely.
          </p>

          <div className="checkout-hof__payment-totals">
            <SummaryLine
              label={`${itemCount} ${itemCount === 1 ? "item" : "items"}`}
              value={formatMoney(totals.subtotal, currency)}
            />
            <SummaryLine
              label="Delivery"
              value={formatMoney(totals.shippingTotal, currency)}
            />
            {totals.discountTotal > 0 ? (
              <SummaryLine
                label="Promo code"
                value={`-${formatMoney(totals.discountTotal, currency)}`}
              />
            ) : null}
            <SummaryLine
              label="Total"
              value={formatMoney(totals.grandTotal, currency)}
              strong
            />
          </div>

          <p className="checkout-hof__terms">
            By placing your order you agree to the website{" "}
            <Link to="/terms-and-conditions">terms and conditions</Link> and you
            can find out more about how we process your data{" "}
            <Link to="/privacy-policy">here</Link>.
          </p>

          <button
            type="button"
            className="checkout-hof__pay-button"
            disabled={payDisabled || processing}
            onClick={onPay}
          >
            {processing ? (
              <>
                <Loader2 size={16} className="checkout-hof__spin" />
                Connecting…
              </>
            ) : (
              "Continue to SumUp"
            )}
          </button>
        </div>
      ) : null}
    </section>
  );
}

function PromoCodePanel({
  value,
  appliedPromoCode,
  discountTotal,
  currency,
  busy,
  onChange,
  onApply,
  onRemove,
}) {
  const activeCode = safeText(appliedPromoCode?.code);

  return (
    <section className="checkout-hof__promo">
      <div className="checkout-hof__promo-head">
        <Tag size={18} />
        <strong>Promo code</strong>
      </div>

      {activeCode ? (
        <div className="checkout-hof__promo-applied">
          <div>
            <span>{activeCode}</span>
            <strong>-{formatMoney(discountTotal, currency)}</strong>
          </div>

          <button type="button" onClick={onRemove} disabled={busy}>
            {busy ? (
              <Loader2 size={15} className="checkout-hof__spin" />
            ) : (
              <X size={15} />
            )}
            Remove
          </button>
        </div>
      ) : (
        <form className="checkout-hof__promo-form" onSubmit={onApply}>
          <input
            type="text"
            value={value}
            placeholder="Enter code"
            autoComplete="off"
            onChange={(event) => onChange(normalizeUpper(event.target.value))}
            disabled={busy}
          />
          <button type="submit" disabled={busy || !safeText(value)}>
            {busy ? (
              <Loader2 size={15} className="checkout-hof__spin" />
            ) : null}
            Apply
          </button>
        </form>
      )}
    </section>
  );
}

function BagSummary({ cart, quote, fulfillmentMethod, currency, loading }) {
  const items = useMemo(
    () => (Array.isArray(cart?.items) ? cart.items : []),
    [cart?.items],
  );

  const totals = useMemo(() => getQuoteTotals({ cart, quote }), [cart, quote]);

  return (
    <aside className="checkout-hof__bag">
      <div className="checkout-hof__bag-card">
        <h2>My bag</h2>

        <div className="checkout-hof__bag-items">
          {items.map((item) => {
            const descriptor = getLineDescriptor(item);

            return (
              <article
                key={item._id || item.id || item.lineKey || item.productId}
                className="checkout-hof__bag-item"
              >
                <div className="checkout-hof__bag-image">
                  {item.image ? (
                    <img
                      src={normalizeImage(item.image)}
                      alt={item.productName || "Product"}
                      onError={(event) => {
                        event.currentTarget.src = FALLBACK_IMAGE;
                      }}
                    />
                  ) : (
                    <ShoppingBag size={18} />
                  )}
                </div>

                <div className="checkout-hof__bag-copy">
                  <strong>{item.productName || "Product"}</strong>
                  {descriptor ? <p>{descriptor}</p> : null}

                  <div className="checkout-hof__bag-price-row">
                    <span>Price:</span>
                    <strong>
                      {formatMoney(item.unitPrice || 0, currency)}
                    </strong>
                  </div>
                </div>

                <div className="checkout-hof__bag-total">
                  <span>Total:</span>
                  <strong>{formatMoney(item.lineTotal || 0, currency)}</strong>
                </div>
              </article>
            );
          })}
        </div>

        <div className="checkout-hof__bag-totals">
          <SummaryLine
            label={`${items.length} ${items.length === 1 ? "item" : "items"}`}
            value={formatMoney(totals.subtotal, currency)}
          />

          <SummaryLine
            label={fulfillmentMethod === "pickup" ? "Pickup" : "Delivery"}
            value={
              loading
                ? "Calculating…"
                : fulfillmentMethod === "pickup"
                  ? "Free"
                  : formatMoney(totals.shippingTotal, currency)
            }
          />

          {totals.discountTotal > 0 ? (
            <SummaryLine
              label={
                quote?.appliedPromoCode?.code || cart?.appliedPromoCode?.code
                  ? `Promo code ${
                      quote?.appliedPromoCode?.code ||
                      cart?.appliedPromoCode?.code
                    }`
                  : "Promo code"
              }
              value={`-${formatMoney(totals.discountTotal, currency)}`}
            />
          ) : null}

          <SummaryLine
            label="Total"
            value={formatMoney(totals.grandTotal, currency)}
            strong
          />
        </div>
      </div>
    </aside>
  );
}

/* -------------------------------------------------------------------------- */
/*                                Shipping Step                               */
/* -------------------------------------------------------------------------- */

function ShippingScreenContent() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const cartState = useSelector((state) => state.cart || {}, shallowEqual);
  const userAuthState = useSelector(
    (state) => state.userAuth || {},
    shallowEqual,
  );
  const userLoginState = useSelector(
    (state) => state.userLogin || {},
    shallowEqual,
  );

  const userInfo = userAuthState.userInfo || userLoginState.userInfo || null;

  const {
    cart,
    checkout: checkoutState,
    quote,
    loading: cartLoading,
    pending = {},
    error: cartError,
  } = cartState;

  const checkout = useMemo(
    () => checkoutState || cart?.checkout || {},
    [checkoutState, cart?.checkout],
  );

  const items = useMemo(
    () => (Array.isArray(cart?.items) ? cart.items : []),
    [cart?.items],
  );

  const [customer, setCustomer] = useState(() =>
    normalizeCustomer(checkout?.customer, userInfo || {}),
  );
  const [fulfillmentMethod, setFulfillmentMethod] = useState(
    normalizeLower(checkout?.fulfillmentMethod || "delivery"),
  );
  const [shippingAddress, setShippingAddress] = useState(() =>
    normalizeAddress(
      checkout?.shippingAddress || getSavedShippingAddress(userInfo),
    ),
  );
  const [pickupDetails, setPickupDetails] = useState(() =>
    normalizePickupDetails(checkout?.pickupDetails),
  );
  const [billing, setBilling] = useState(() => ({
    useShippingAsBilling:
      typeof checkout?.billing?.useShippingAsBilling === "boolean"
        ? checkout.billing.useShippingAsBilling
        : true,
    address: normalizeAddress(checkout?.billing?.address),
  }));
  const [saveShippingAsDefault, setSaveShippingAsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshingBasket, setRefreshingBasket] = useState(false);
  const [localIssues, setLocalIssues] = useState([]);

  const mountedRef = useRef(true);
  const lastQuoteKeyRef = useRef("");
  const lastCheckoutSyncRef = useRef("");

  const currency = useMemo(
    () => normalizeUpper(quote?.currency || cart?.currency || "GBP"),
    [quote?.currency, cart?.currency],
  );

  const checkoutCountry = useMemo(
    () =>
      fulfillmentMethod === "delivery"
        ? normalizeUpper(shippingAddress.country || "GB")
        : "GB",
    [fulfillmentMethod, shippingAddress.country],
  );

  const quoteParams = useMemo(
    () => ({
      fulfillmentMethod,
      country: checkoutCountry,
      currency: "GBP",
    }),
    [checkoutCountry, fulfillmentMethod],
  );

  const quoteKey = useMemo(() => {
    const itemKey = items
      .map((item) =>
        [
          item._id || item.id || item.lineKey || item.productId || "",
          item.qty || 1,
          item.unitPrice || 0,
          item.lineTotal || 0,
        ].join(":"),
      )
      .join("|");

    return `${fulfillmentMethod}:${checkoutCountry}:GBP:${itemKey}`;
  }, [checkoutCountry, fulfillmentMethod, items]);

  const quoteRequest = useMemo(
    () => ({
      key: quoteKey,
      params: quoteParams,
    }),
    [quoteKey, quoteParams],
  );

  const debouncedQuoteRequest = useDebounce(quoteRequest, 300);

  const cartReadyForCheckout = useMemo(() => {
    if (typeof quote?.readyForCheckout === "boolean") {
      return quote.readyForCheckout;
    }

    if (typeof cart?.readyForCheckout === "boolean") {
      return cart.readyForCheckout;
    }

    return (
      items.length > 0 && !cart?.hasUnavailableItems && !cart?.hasPriceChanges
    );
  }, [
    cart?.hasPriceChanges,
    cart?.hasUnavailableItems,
    cart?.readyForCheckout,
    items.length,
    quote?.readyForCheckout,
  ]);

  const deliveryComplete = useMemo(
    () =>
      fulfillmentMethod === "pickup"
        ? pickupComplete(pickupDetails)
        : addressComplete(shippingAddress),
    [fulfillmentMethod, pickupDetails, shippingAddress],
  );

  const billingComplete = useMemo(
    () =>
      billing.useShippingAsBilling
        ? deliveryComplete
        : addressComplete(billing.address),
    [billing.address, billing.useShippingAsBilling, deliveryComplete],
  );

  const customerComplete = useMemo(
    () => Boolean(customer.name && customer.email),
    [customer.email, customer.name],
  );

  const canContinue = useMemo(
    () =>
      items.length > 0 &&
      cartReadyForCheckout &&
      deliveryComplete &&
      billingComplete &&
      customerComplete,
    [
      billingComplete,
      cartReadyForCheckout,
      customerComplete,
      deliveryComplete,
      items.length,
    ],
  );

  const busy = useMemo(
    () =>
      Boolean(
        cartLoading ||
          pending.quote ||
          pending.validate ||
          pending.checkout ||
          pending.checkoutCustomer ||
          pending.checkoutFulfillment ||
          pending.checkoutShippingAddress ||
          pending.checkoutPickupDetails ||
          pending.checkoutBilling ||
          saving,
      ),
    [
      cartLoading,
      pending.checkout,
      pending.checkoutBilling,
      pending.checkoutCustomer,
      pending.checkoutFulfillment,
      pending.checkoutPickupDetails,
      pending.checkoutShippingAddress,
      pending.quote,
      pending.validate,
      saving,
    ],
  );

  const visibleIssues = useMemo(() => localIssues, [localIssues]);

  useEffect(() => {
    mountedRef.current = true;

    dispatch(getCart());
    dispatch(getCheckout());

    return () => {
      mountedRef.current = false;

      dispatch(resetCartError());
      dispatch(resetCartMeta());
    };
  }, [dispatch]);

  useEffect(() => {
    const key = debouncedQuoteRequest?.key;
    const params = debouncedQuoteRequest?.params;

    if (!items.length) return;
    if (!key || lastQuoteKeyRef.current === key) return;

    lastQuoteKeyRef.current = key;

    dispatch(getCheckoutQuote(params)).catch(() => {});
  }, [debouncedQuoteRequest, dispatch, items.length]);

  useEffect(() => {
    const syncKey = JSON.stringify({
      checkout,
      userEmail: userInfo?.email || "",
    });

    if (lastCheckoutSyncRef.current === syncKey) return;

    lastCheckoutSyncRef.current = syncKey;

    setCustomer(normalizeCustomer(checkout?.customer, userInfo || {}));
    setFulfillmentMethod(
      normalizeLower(checkout?.fulfillmentMethod || "delivery"),
    );
    const checkoutAddress = normalizeAddress(checkout?.shippingAddress);
    const savedAddress = getSavedShippingAddress(userInfo || {});
    setShippingAddress(
      hasUsefulAddress(checkoutAddress) ? checkoutAddress : savedAddress,
    );
    setPickupDetails(normalizePickupDetails(checkout?.pickupDetails));

    setBilling({
      useShippingAsBilling:
        typeof checkout?.billing?.useShippingAsBilling === "boolean"
          ? checkout.billing.useShippingAsBilling
          : true,
      address: normalizeAddress(checkout?.billing?.address),
    });
  }, [checkout, userInfo]);

  useEffect(() => {
    if (cartError) {
      toast.error(
        normalizeErrorMessage(
          cartError,
          "We could not load checkout right now. Please try again later.",
        ),
      );
    }
  }, [cartError]);

  const validateShippingStep = useCallback(() => {
    const issues = [];

    if (!items.length) {
      issues.push({ code: "BASKET_EMPTY", message: "Your basket is empty." });
    }

    if (!cartReadyForCheckout) {
      issues.push({
        code: "BASKET_NOT_READY",
        message: "Your basket has items that need attention.",
      });
    }

    if (!customer.name || !customer.email) {
      issues.push({
        code: "CUSTOMER_REQUIRED",
        message: "Please add your contact details.",
      });
    }

    if (!deliveryComplete) {
      issues.push({
        code: "FULFILMENT_REQUIRED",
        message:
          fulfillmentMethod === "pickup"
            ? "Please complete your pickup details."
            : "Please complete your UK delivery address.",
      });
    }

    if (!billingComplete) {
      issues.push({
        code: "BILLING_REQUIRED",
        message: "Please complete your billing address.",
      });
    }

    return issues;
  }, [
    billingComplete,
    cartReadyForCheckout,
    customer.email,
    customer.name,
    deliveryComplete,
    fulfillmentMethod,
    items.length,
  ]);

  const refreshBasket = useCallback(async () => {
    setRefreshingBasket(true);

    try {
      await dispatch(getCart());
      await dispatch(getCheckout());
      await dispatch(getCheckoutQuote(quoteParams));

      setLocalIssues([]);
      toast.success("Basket refreshed.");
    } catch (error) {
      toast.error(
        normalizeErrorMessage(
          error,
          "We could not refresh your basket right now. Please try again later.",
        ),
      );
    } finally {
      if (mountedRef.current) setRefreshingBasket(false);
    }
  }, [dispatch, quoteParams]);

  const saveShippingDraft = useCallback(async () => {
    await dispatch(saveCustomerDetails(customer));
    await dispatch(saveFulfillmentMethod(fulfillmentMethod));

    if (fulfillmentMethod === "pickup") {
      await dispatch(savePickupDetails(pickupDetails));
    } else {
      await dispatch(saveShippingAddress(shippingAddress));

      if (saveShippingAsDefault) {
        await dispatch(updateDefaultShippingAddress(shippingAddress));
      }
    }

    await dispatch(saveBillingOptions(billing));
  }, [
    billing,
    customer,
    dispatch,
    fulfillmentMethod,
    pickupDetails,
    saveShippingAsDefault,
    shippingAddress,
  ]);

  const handleContinue = useCallback(async () => {
    if (busy) return;

    const localValidationIssues = validateShippingStep();

    if (localValidationIssues.length) {
      setLocalIssues(localValidationIssues);
      toast.warn(localValidationIssues[0].message);
      return;
    }

    setSaving(true);
    setLocalIssues([]);

    try {
      await saveShippingDraft();

      const validation = await dispatch(validateCart());

      if (validation?.ok === false) {
        const issues = getErrorIssues(validation);
        if (issues.length) setLocalIssues(issues);

        throw Object.assign(
          new Error(
            validation?.message ||
              issues[0]?.message ||
              "Your basket needs attention.",
          ),
          {
            message:
              validation?.message ||
              issues[0]?.message ||
              "Your basket needs attention.",
            issues,
          },
        );
      }

      await dispatch(getCheckoutQuote(quoteParams));
      navigate("/checkout");
    } catch (error) {
      const issues = getErrorIssues(error);
      if (issues.length) setLocalIssues(issues);

      toast.error(
        normalizeErrorMessage(
          error,
          "We could not save your shipping details right now. Please try again later.",
        ),
      );
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, [
    busy,
    dispatch,
    navigate,
    quoteParams,
    saveShippingDraft,
    validateShippingStep,
  ]);

  if (cartLoading && !cart) {
    return (
      <main className="checkout-hof checkout-hof--state">
        <Meta title="Shipping | Academy Costumes London" robots="noindex, nofollow" />

        <section className="checkout-hof__state-card">
          <Loader2 size={22} className="checkout-hof__spin" />
          <p>Preparing checkout…</p>
        </section>
      </main>
    );
  }

  if (!items.length) {
    return (
      <main className="checkout-hof checkout-hof--state">
        <Meta title="Shipping | Academy Costumes London" robots="noindex, nofollow" />

        <section className="checkout-hof__state-card">
          <ShoppingBag size={24} />
          <h1>Your basket is empty</h1>
          <p>Add something to your basket before checkout.</p>
          <Link to="/" className="checkout-hof__primary-link">
            Continue shopping
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="checkout-hof checkout-hof--shipping">
      <Meta title="Shipping | Academy Costumes London" robots="noindex, nofollow" />

      <header className="checkout-hof__header">
        <h1>Shipping</h1>
        <p>
          Add contact, delivery and billing details first. Payment stays on the
          next screen.
        </p>
      </header>

      <div className="checkout-hof__layout">
        <div className="checkout-hof__timeline">
          <IssuesPanel
            issues={visibleIssues}
            refreshing={refreshingBasket}
            onRefresh={refreshBasket}
          />

          <CheckoutStep title="Contact">
            <div className="checkout-hof__field-grid">
              <TextField
                label="Full name"
                required
                value={customer.name}
                autoComplete="name"
                onChange={(value) =>
                  setCustomer((prev) => ({ ...prev, name: value }))
                }
              />

              <TextField
                label="Email"
                type="email"
                required
                value={customer.email}
                autoComplete="email"
                onChange={(value) =>
                  setCustomer((prev) => ({
                    ...prev,
                    email: normalizeLower(value),
                  }))
                }
              />

              <TextField
                label="Phone"
                value={customer.phone}
                autoComplete="tel"
                onChange={(value) =>
                  setCustomer((prev) => ({ ...prev, phone: value }))
                }
              />
            </div>
          </CheckoutStep>

          <CheckoutStep title="Delivery">
            <div className="checkout-hof__choice-row">
              <button
                type="button"
                className={clsx(
                  "checkout-hof__choice",
                  fulfillmentMethod === "delivery" && "is-selected",
                )}
                onClick={() => setFulfillmentMethod("delivery")}
              >
                <Truck size={18} />
                <span>UK home delivery</span>
              </button>

              <button
                type="button"
                className={clsx(
                  "checkout-hof__choice",
                  fulfillmentMethod === "pickup" && "is-selected",
                )}
                onClick={() => setFulfillmentMethod("pickup")}
              >
                <PackageCheck size={18} />
                <span>Pickup</span>
              </button>
            </div>

            {fulfillmentMethod === "pickup" ? (
              <div className="checkout-hof__field-grid">
                <TextField
                  label="Pickup date"
                  required
                  value={pickupDetails.date}
                  onChange={(value) =>
                    setPickupDetails((prev) => ({ ...prev, date: value }))
                  }
                />

                <TextField
                  label="Time slot"
                  required
                  value={pickupDetails.timeSlot}
                  onChange={(value) =>
                    setPickupDetails((prev) => ({
                      ...prev,
                      timeSlot: value,
                    }))
                  }
                />

                <TextField
                  label="Instructions"
                  value={pickupDetails.instructions}
                  onChange={(value) =>
                    setPickupDetails((prev) => ({
                      ...prev,
                      instructions: value,
                    }))
                  }
                />
              </div>
            ) : (
              <>
                <AddressFields
                  address={shippingAddress}
                  onChange={setShippingAddress}
                  prefix="shipping"
                />

                {userInfo ? (
                  <label className="checkout-hof__checkbox">
                    <input
                      type="checkbox"
                      checked={saveShippingAsDefault}
                      onChange={(event) =>
                        setSaveShippingAsDefault(event.target.checked)
                      }
                    />
                    <span>Save this delivery address for next time</span>
                  </label>
                ) : null}
              </>
            )}
          </CheckoutStep>

          <CheckoutStep title="Billing">
            <label className="checkout-hof__checkbox">
              <input
                type="checkbox"
                checked={billing.useShippingAsBilling}
                onChange={(event) =>
                  setBilling((prev) => ({
                    ...prev,
                    useShippingAsBilling: event.target.checked,
                  }))
                }
              />
              <span>Use delivery address as billing address</span>
            </label>

            {!billing.useShippingAsBilling ? (
              <AddressFields
                address={billing.address}
                onChange={(address) =>
                  setBilling((prev) => ({ ...prev, address }))
                }
                prefix="billing"
              />
            ) : null}
          </CheckoutStep>

          <section className="checkout-hof__action-row">
            <Link to="/cart" className="checkout-hof__secondary-action">
              <ArrowLeft size={17} />
              Back to bag
            </Link>

            <button
              type="button"
              className="checkout-hof__primary-action"
              disabled={busy || !canContinue}
              onClick={handleContinue}
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="checkout-hof__spin" />
                  Saving…
                </>
              ) : (
                <>
                  Continue to checkout
                  <ArrowRight size={17} />
                </>
              )}
            </button>
          </section>

          {!canContinue ? (
            <p className="checkout-hof__action-hint">
              Complete the required shipping details before payment.
            </p>
          ) : null}
        </div>

        <BagSummary
          cart={cart}
          quote={quote}
          fulfillmentMethod={fulfillmentMethod}
          currency={currency}
          loading={Boolean(pending.quote)}
        />
      </div>
    </main>
  );
}

/* -------------------------------------------------------------------------- */
/*                              Checkout Content                              */
/* -------------------------------------------------------------------------- */

function CheckoutScreenContent() {
  const dispatch = useDispatch();

  const cartState = useSelector((state) => state.cart || {}, shallowEqual);
  const orderCreateState = useSelector(selectOrderCreateState, shallowEqual);
  const createdOrder = useSelector(selectCreatedOrder, shallowEqual);
  const createdOrderNumber = useSelector(selectCreatedOrderNumber);

  const paymentConfig = useSelector(selectPaymentConfig, shallowEqual);
  const anyPaymentBusy = useSelector(selectAnyPaymentBusy);

  const userAuthState = useSelector(
    (state) => state.userAuth || {},
    shallowEqual,
  );
  const userLoginState = useSelector(
    (state) => state.userLogin || {},
    shallowEqual,
  );

  const userInfo = userAuthState.userInfo || userLoginState.userInfo || null;

  const {
    cart,
    checkout: checkoutState,
    quote,
    loading: cartLoading,
    pending = {},
    error: cartError,
  } = cartState;

  const checkout = useMemo(
    () => checkoutState || cart?.checkout || {},
    [checkoutState, cart?.checkout],
  );

  const items = useMemo(
    () => (Array.isArray(cart?.items) ? cart.items : []),
    [cart?.items],
  );

  const [customer, setCustomer] = useState(() =>
    normalizeCustomer(checkout?.customer, userInfo || {}),
  );
  const [fulfillmentMethod, setFulfillmentMethod] = useState(
    normalizeLower(checkout?.fulfillmentMethod || "delivery"),
  );
  const [shippingAddress, setShippingAddress] = useState(() =>
    normalizeAddress(
      checkout?.shippingAddress || getSavedShippingAddress(userInfo),
    ),
  );
  const [pickupDetails, setPickupDetails] = useState(() =>
    normalizePickupDetails(checkout?.pickupDetails),
  );
  const [billing, setBilling] = useState(() => ({
    useShippingAsBilling:
      typeof checkout?.billing?.useShippingAsBilling === "boolean"
        ? checkout.billing.useShippingAsBilling
        : true,
    address: normalizeAddress(checkout?.billing?.address),
  }));
  const [acknowledgements, setAcknowledgements] = useState(() => ({
    textileNoReturnAccepted: Boolean(
      checkout?.acknowledgements?.textileNoReturnAccepted,
    ),
    standardReturnsAccepted: true,
  }));
  const [paymentProvider, setPaymentProvider] = useState(
    normalizeLower(checkout?.paymentMethod || checkout?.paymentProvider),
  );
  const [notes, setNotes] = useState(safeText(checkout?.notes));
  const [promoCodeInput, setPromoCodeInput] = useState(
    safeText(cart?.appliedPromoCode?.code),
  );
  const [submitting, setSubmitting] = useState(false);
  const [refreshingBasket, setRefreshingBasket] = useState(false);
  const [localIssues, setLocalIssues] = useState([]);

  const mountedRef = useRef(true);
  const submittingRef = useRef(false);
  const lastQuoteKeyRef = useRef("");
  const lastCheckoutSyncRef = useRef("");

  const paymentOptions = useMemo(
    () => getPaymentOptions(paymentConfig),
    [paymentConfig],
  );

  const currency = useMemo(
    () => normalizeUpper(quote?.currency || cart?.currency || "GBP"),
    [quote?.currency, cart?.currency],
  );

  const totals = useMemo(() => getQuoteTotals({ cart, quote }), [cart, quote]);

  const appliedPromoCode = useMemo(
    () => quote?.appliedPromoCode || cart?.appliedPromoCode || null,
    [cart?.appliedPromoCode, quote?.appliedPromoCode],
  );

  const itemCount = useMemo(
    () => Number(quote?.itemCount ?? cart?.itemCount ?? items.length),
    [quote?.itemCount, cart?.itemCount, items.length],
  );

  const needsTextileAcknowledgement = useMemo(
    () => hasTextilePolicyLines(cart) && !(cart?.items || []).every((item) => ["haberdashery", "ready-made", "textiles"].includes(item.shopKey)),
    [cart],
  );

  const checkoutCountry = useMemo(
    () =>
      fulfillmentMethod === "delivery"
        ? normalizeUpper(shippingAddress.country || "GB")
        : "GB",
    [fulfillmentMethod, shippingAddress.country],
  );

  const checkoutAcknowledgements = useMemo(
    () =>
      buildAcknowledgements({
        needsTextileAcknowledgement,
        textileNoReturnAccepted: acknowledgements.textileNoReturnAccepted,
      }),
    [acknowledgements.textileNoReturnAccepted, needsTextileAcknowledgement],
  );

  const quoteParams = useMemo(
    () => ({
      fulfillmentMethod,
      country: checkoutCountry,
      currency: "GBP",
    }),
    [checkoutCountry, fulfillmentMethod],
  );

  const quoteKey = useMemo(() => {
    const itemKey = items
      .map((item) =>
        [
          item._id || item.id || item.lineKey || item.productId || "",
          item.qty || 1,
          item.unitPrice || 0,
          item.lineTotal || 0,
        ].join(":"),
      )
      .join("|");

    return `${fulfillmentMethod}:${checkoutCountry}:GBP:${itemKey}`;
  }, [checkoutCountry, fulfillmentMethod, items]);

  const quoteRequest = useMemo(
    () => ({
      key: quoteKey,
      params: quoteParams,
    }),
    [quoteKey, quoteParams],
  );

  const debouncedQuoteRequest = useDebounce(quoteRequest, 300);

  const cartReadyForCheckout = useMemo(() => {
    if (typeof quote?.readyForCheckout === "boolean") {
      return quote.readyForCheckout;
    }

    if (typeof cart?.readyForCheckout === "boolean") {
      return cart.readyForCheckout;
    }

    return (
      items.length > 0 && !cart?.hasUnavailableItems && !cart?.hasPriceChanges
    );
  }, [
    cart?.hasPriceChanges,
    cart?.hasUnavailableItems,
    cart?.readyForCheckout,
    items.length,
    quote?.readyForCheckout,
  ]);

  const deliveryComplete = useMemo(
    () =>
      fulfillmentMethod === "pickup"
        ? pickupComplete(pickupDetails)
        : addressComplete(shippingAddress),
    [fulfillmentMethod, pickupDetails, shippingAddress],
  );

  const billingComplete = useMemo(
    () =>
      billing.useShippingAsBilling
        ? deliveryComplete
        : addressComplete(billing.address),
    [billing.address, billing.useShippingAsBilling, deliveryComplete],
  );

  const customerComplete = useMemo(
    () => Boolean(customer.name && customer.email),
    [customer.email, customer.name],
  );

  const shippingStepComplete = useMemo(
    () => deliveryComplete && billingComplete && customerComplete,
    [billingComplete, customerComplete, deliveryComplete],
  );

  const selectedPaymentUnavailable = useMemo(
    () =>
      Boolean(
        paymentProvider &&
        paymentOptions.length &&
        !paymentOptions.some((option) => option.provider === paymentProvider),
      ),
    [paymentOptions, paymentProvider],
  );

  const canPay = useMemo(
    () =>
      items.length > 0 &&
      cartReadyForCheckout &&
      shippingStepComplete &&
      paymentProvider &&
      !selectedPaymentUnavailable &&
      (!needsTextileAcknowledgement ||
        acknowledgements.textileNoReturnAccepted),
    [
      acknowledgements.textileNoReturnAccepted,
      cartReadyForCheckout,
      items.length,
      needsTextileAcknowledgement,
      paymentProvider,
      selectedPaymentUnavailable,
      shippingStepComplete,
    ],
  );

  const busy = useMemo(
    () =>
      Boolean(
        cartLoading ||
        pending.quote ||
        pending.promoCode ||
        pending.validate ||
        pending.checkout ||
        orderCreateState?.loading ||
        anyPaymentBusy ||
        submitting,
      ),
    [
      anyPaymentBusy,
      cartLoading,
      orderCreateState?.loading,
      pending.checkout,
      pending.promoCode,
      pending.quote,
      pending.validate,
      submitting,
    ],
  );

  const visibleIssues = useMemo(() => {
    if (localIssues.length) return localIssues;

    return getErrorIssues({
      issues: orderCreateState?.issues,
      failures: orderCreateState?.failures,
      inventoryFailures: orderCreateState?.inventoryFailures,
      message: orderCreateState?.error,
    });
  }, [
    localIssues,
    orderCreateState?.error,
    orderCreateState?.failures,
    orderCreateState?.inventoryFailures,
    orderCreateState?.issues,
  ]);

  useEffect(() => {
    mountedRef.current = true;

    dispatch(clearCurrentPayment());
    dispatch(getCart());
    dispatch(getCheckout());
    dispatch(getPaymentConfig());

    return () => {
      mountedRef.current = false;
      submittingRef.current = false;

      dispatch(resetCartError());
      dispatch(resetCartMeta());
      dispatch(resetOrderCreate());
      dispatch(resetOrderError());
      dispatch(resetOrderMeta());
      dispatch(resetPaymentError());
      dispatch(resetPaymentMeta());
    };
  }, [dispatch]);

  useEffect(() => {
    const key = debouncedQuoteRequest?.key;
    const params = debouncedQuoteRequest?.params;

    if (!items.length) return;
    if (!key || lastQuoteKeyRef.current === key) return;

    lastQuoteKeyRef.current = key;

    dispatch(getCheckoutQuote(params)).catch(() => {});
  }, [debouncedQuoteRequest, dispatch, items.length]);

  useEffect(() => {
    const syncKey = JSON.stringify({
      checkout,
      userEmail: userInfo?.email || "",
    });

    if (lastCheckoutSyncRef.current === syncKey) return;

    lastCheckoutSyncRef.current = syncKey;

    setCustomer(normalizeCustomer(checkout?.customer, userInfo || {}));
    setFulfillmentMethod(
      normalizeLower(checkout?.fulfillmentMethod || "delivery"),
    );
    const checkoutAddress = normalizeAddress(checkout?.shippingAddress);
    const savedAddress = getSavedShippingAddress(userInfo || {});
    setShippingAddress(
      hasUsefulAddress(checkoutAddress) ? checkoutAddress : savedAddress,
    );
    setPickupDetails(normalizePickupDetails(checkout?.pickupDetails));

    setBilling({
      useShippingAsBilling:
        typeof checkout?.billing?.useShippingAsBilling === "boolean"
          ? checkout.billing.useShippingAsBilling
          : true,
      address: normalizeAddress(checkout?.billing?.address),
    });

    setAcknowledgements({
      textileNoReturnAccepted: Boolean(
        checkout?.acknowledgements?.textileNoReturnAccepted,
      ),
      standardReturnsAccepted: true,
    });

    setPaymentProvider(
      normalizeLower(checkout?.paymentMethod || checkout?.paymentProvider),
    );

    setNotes(safeText(checkout?.notes));
  }, [checkout, userInfo]);

  useEffect(() => {
    setPromoCodeInput(safeText(appliedPromoCode?.code));
  }, [appliedPromoCode?.code]);

  useEffect(() => {
    if (!paymentOptions.length) return;

    const selectedExists = paymentOptions.some(
      (option) => option.provider === paymentProvider,
    );

    if (!selectedExists) {
      setPaymentProvider(paymentOptions[0].provider);
    }
  }, [paymentOptions, paymentProvider]);

  useEffect(() => {
    if (cartError) {
      toast.error(
        normalizeErrorMessage(
          cartError,
          "We could not load checkout right now. Please try again later.",
        ),
      );
    }
  }, [cartError]);

  const validateLocalCheckout = useCallback(() => {
    const issues = [];

    if (!items.length) {
      issues.push({ code: "BASKET_EMPTY", message: "Your basket is empty." });
    }

    if (!cartReadyForCheckout) {
      issues.push({
        code: "BASKET_NOT_READY",
        message: "Your basket has items that need attention.",
      });
    }

    if (!customer.name || !customer.email) {
      issues.push({
        code: "CUSTOMER_REQUIRED",
        message: "Please add your contact details.",
      });
    }

    if (!deliveryComplete) {
      issues.push({
        code: "FULFILMENT_REQUIRED",
        message:
          fulfillmentMethod === "pickup"
            ? "Please complete your pickup details."
            : "Please complete your UK delivery address.",
      });
    }

    if (!billingComplete) {
      issues.push({
        code: "BILLING_REQUIRED",
        message: "Please complete your billing address.",
      });
    }

    if (!paymentProvider || selectedPaymentUnavailable) {
      issues.push({
        code: "PAYMENT_REQUIRED",
        message: "Please choose an available payment method.",
      });
    }

    if (
      needsTextileAcknowledgement &&
      !acknowledgements.textileNoReturnAccepted
    ) {
      issues.push({
        code: "TEXTILE_POLICY_REQUIRED",
        message: "Please confirm the cut-to-order textile policy.",
      });
    }

    return issues;
  }, [
    acknowledgements.textileNoReturnAccepted,
    billingComplete,
    cartReadyForCheckout,
    customer.email,
    customer.name,
    deliveryComplete,
    fulfillmentMethod,
    items.length,
    needsTextileAcknowledgement,
    paymentProvider,
    selectedPaymentUnavailable,
  ]);

  const refreshBasket = useCallback(async () => {
    setRefreshingBasket(true);

    try {
      await dispatch(getCart());
      await dispatch(getCheckout());
      await dispatch(getCheckoutQuote(quoteParams));

      setLocalIssues([]);
      toast.success("Basket refreshed.");
    } catch (error) {
      toast.error(
        normalizeErrorMessage(
          error,
          "We could not refresh your basket right now. Please try again later.",
        ),
      );
    } finally {
      if (mountedRef.current) setRefreshingBasket(false);
    }
  }, [dispatch, quoteParams]);

  const saveCheckoutDraft = useCallback(async () => {
    await dispatch(saveCustomerDetails(customer));
    await dispatch(saveFulfillmentMethod(fulfillmentMethod));

    if (fulfillmentMethod === "pickup") {
      await dispatch(savePickupDetails(pickupDetails));
    } else {
      await dispatch(saveShippingAddress(shippingAddress));
    }

    await dispatch(saveBillingOptions(billing));
    await dispatch(saveCheckoutAcknowledgements(checkoutAcknowledgements));
    await dispatch(savePaymentMethod(paymentProvider));
    await dispatch(saveCheckoutNotes(notes));
  }, [
    billing,
    checkoutAcknowledgements,
    customer,
    dispatch,
    fulfillmentMethod,
    notes,
    paymentProvider,
    pickupDetails,
    shippingAddress,
  ]);

  const createOrderAndPayment = useCallback(async () => {
    await saveCheckoutDraft();

    const validation = await dispatch(validateCart());

    if (validation?.ok === false) {
      const issues = getErrorIssues(validation);
      if (issues.length) setLocalIssues(issues);

      throw Object.assign(
        new Error(
          validation?.message ||
            issues[0]?.message ||
            "Your basket needs attention.",
        ),
        {
          message:
            validation?.message ||
            issues[0]?.message ||
            "Your basket needs attention.",
          issues,
        },
      );
    }

    await dispatch(getCheckoutQuote(quoteParams));

    const created = await dispatch(
      createOrder({
        provider: paymentProvider,
        paymentProvider,
        paymentMethod: paymentProvider,
        fulfillmentMethod,
        deliveryOptionId: quote?.deliveryOption?.id || "",
        currency: "GBP",
        saveShippingAddressAsDefault: false,

        acknowledgements: checkoutAcknowledgements,
        checkoutAcknowledgements,
        returnPolicyAcknowledgements: checkoutAcknowledgements,
      }),
    );

    const order = getOrderFromAnyPayload(created, createdOrder);

    const orderNumber = getOrderNumberFromAny(
      created,
      {
        orderNumber: createdOrderNumber,
        order: createdOrder,
      },
      { order },
    );

    if (!orderNumber) {
      throw new Error("Order was created, but no order number was returned.");
    }

    const payment = await dispatch(
      createPaymentForExistingOrder({
        orderNumber,
        provider: paymentProvider,
        paymentProvider,
        paymentMethod: paymentProvider,
        currency: "GBP",
      }),
    );

    return {
      order,
      orderNumber,
      payment,
    };
  }, [
    checkoutAcknowledgements,
    createdOrder,
    createdOrderNumber,
    dispatch,
    fulfillmentMethod,
    paymentProvider,
    quote?.deliveryOption?.id,
    quoteParams,
    saveCheckoutDraft,
  ]);

  const handlePaymentProviderSelect = useCallback((provider) => {
    setPaymentProvider(provider);
    setLocalIssues([]);
  }, []);

  const handleApplyPromoCode = useCallback(
    async (event) => {
      event.preventDefault();

      const code = normalizeUpper(promoCodeInput);

      if (!code || pending.promoCode) return;

      try {
        await dispatch(applyPromoCode(code));
        await dispatch(getCheckoutQuote(quoteParams));
        toast.success(`Promo code ${code} applied.`);
      } catch (error) {
        toast.error(
          normalizeErrorMessage(
            error,
            "We could not apply that promo code right now. Please try again later.",
          ),
        );
      }
    },
    [dispatch, pending.promoCode, promoCodeInput, quoteParams],
  );

  const handleRemovePromoCode = useCallback(async () => {
    if (pending.promoCode) return;

    try {
      await dispatch(removePromoCode());
      await dispatch(getCheckoutQuote(quoteParams));
      setPromoCodeInput("");
      toast.success("Promo code removed.");
    } catch (error) {
      toast.error(
        normalizeErrorMessage(
          error,
          "We could not remove that promo code right now. Please try again later.",
        ),
      );
    }
  }, [dispatch, pending.promoCode, quoteParams]);

  const handlePay = useCallback(async () => {
    if (busy || submittingRef.current) {
      return;
    }

    const localValidationIssues = validateLocalCheckout();

    if (localValidationIssues.length) {
      setLocalIssues(localValidationIssues);
      toast.warn(localValidationIssues[0].message);
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setLocalIssues([]);

    try {
      const { payment } = await createOrderAndPayment();
      const checkoutUrl = getApprovalUrlFromAny(payment);
      const providerName = getPaymentProviderName(paymentProvider);

      if (!checkoutUrl) {
        toast.error(`${providerName} checkout link was not returned.`);
        return;
      }

      window.location.assign(checkoutUrl);
    } catch (error) {
      const issues = getErrorIssues(error);

      if (issues.length) setLocalIssues(issues);

      toast.error(
        normalizeErrorMessage(
          error,
          "We could not complete checkout right now. Please try again later.",
        ),
      );
    } finally {
      submittingRef.current = false;
      if (mountedRef.current) setSubmitting(false);
    }
  }, [
    busy,
    createOrderAndPayment,
    paymentProvider,
    validateLocalCheckout,
  ]);

  if (cartLoading && !cart) {
    return (
      <main className="checkout-hof checkout-hof--state">
        <Meta title="Checkout | Academy Costumes London" robots="noindex, nofollow" />

        <section className="checkout-hof__state-card">
          <Loader2 size={22} className="checkout-hof__spin" />
          <p>Preparing checkout…</p>
        </section>
      </main>
    );
  }

  if (!items.length) {
    return (
      <main className="checkout-hof checkout-hof--state">
        <Meta title="Checkout | Academy Costumes London" robots="noindex, nofollow" />

        <section className="checkout-hof__state-card">
          <ShoppingBag size={24} />
          <h1>Your basket is empty</h1>
          <p>Add something to your basket before checkout.</p>
          <Link to="/" className="checkout-hof__primary-link">
            Continue shopping
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="checkout-hof">
      <Meta title="Checkout | Academy Costumes London" robots="noindex, nofollow" />

      <header className="checkout-hof__header">
        <h1>Checkout</h1>
        <p>
          Review your saved shipping details, choose payment, and place the
          order securely in GBP.
        </p>
      </header>

      <div className="checkout-hof__layout">
        <div className="checkout-hof__timeline">
          <IssuesPanel
            issues={visibleIssues}
            refreshing={refreshingBasket}
            onRefresh={refreshBasket}
          />

          <CheckoutStep title="Shipping">
            <ShippingReview
              customer={customer}
              fulfillmentMethod={fulfillmentMethod}
              shippingAddress={shippingAddress}
              pickupDetails={pickupDetails}
              billing={billing}
            />

            <Link to="/checkout/shipping" className="checkout-hof__edit-link">
              <PencilLine size={16} />
              Edit shipping details
            </Link>

            {!shippingStepComplete ? (
              <p className="checkout-hof__hint">
                Complete shipping details before choosing payment.
              </p>
            ) : null}
          </CheckoutStep>

          <CheckoutStep title="Payment">
            <div className="checkout-hof__payment-list">
              {paymentOptions.some(
                (option) => option.provider === PAYMENT_PROVIDERS.STRIPE,
              ) ? (
                <PaymentMethodPanel
                  provider={PAYMENT_PROVIDERS.STRIPE}
                  selected={paymentProvider === PAYMENT_PROVIDERS.STRIPE}
                  disabled={busy}
                  payDisabled={busy || !canPay}
                  totals={totals}
                  currency={currency}
                  itemCount={itemCount}
                  processing={
                    submitting && paymentProvider === PAYMENT_PROVIDERS.STRIPE
                  }
                  onSelect={handlePaymentProviderSelect}
                  onPay={handlePay}
                />
              ) : null}

              {paymentOptions.some(
                (option) => option.provider === PAYMENT_PROVIDERS.PAYPAL,
              ) ? (
                <PaymentMethodPanel
                  provider={PAYMENT_PROVIDERS.PAYPAL}
                  selected={paymentProvider === PAYMENT_PROVIDERS.PAYPAL}
                  disabled={busy}
                  payDisabled={busy || !canPay}
                  totals={totals}
                  currency={currency}
                  itemCount={itemCount}
                  processing={
                    submitting && paymentProvider === PAYMENT_PROVIDERS.PAYPAL
                  }
                  onSelect={handlePaymentProviderSelect}
                  onPay={handlePay}
                />
              ) : null}

              {paymentOptions.some(
                (option) => option.provider === PAYMENT_PROVIDERS.SUMUP,
              ) ? (
                <PaymentMethodPanel
                  provider={PAYMENT_PROVIDERS.SUMUP}
                  selected={paymentProvider === PAYMENT_PROVIDERS.SUMUP}
                  disabled={busy}
                  payDisabled={busy || !canPay}
                  totals={totals}
                  currency={currency}
                  itemCount={itemCount}
                  processing={
                    submitting && paymentProvider === PAYMENT_PROVIDERS.SUMUP
                  }
                  onSelect={handlePaymentProviderSelect}
                  onPay={handlePay}
                />
              ) : null}
            </div>

            <PromoCodePanel
              value={promoCodeInput}
              appliedPromoCode={appliedPromoCode}
              discountTotal={totals.discountTotal}
              currency={currency}
              busy={Boolean(pending.promoCode)}
              onChange={setPromoCodeInput}
              onApply={handleApplyPromoCode}
              onRemove={handleRemovePromoCode}
            />

            <div className="checkout-hof__policies">
              {quote?.shopCancellationInformation ? <details>
                <summary>14-day Shop cancellation rights</summary>
                <p>{quote.shopCancellationInformation}</p>
                <p>{quote.shopModelCancellationForm}</p>
                <p>{quote.shopReturnContact?.name}, {quote.shopReturnContact?.address}. {quote.shopReturnContact?.email}</p>
                <Link to="/terms-and-conditions#shop-cancellation">Shop cancellation terms</Link>
              </details> : null}
              {needsTextileAcknowledgement ? (
                <label className="checkout-hof__checkbox">
                  <input
                    type="checkbox"
                    checked={acknowledgements.textileNoReturnAccepted}
                    onChange={(event) =>
                      setAcknowledgements((prev) => ({
                        ...prev,
                        textileNoReturnAccepted: event.target.checked,
                        standardReturnsAccepted: true,
                      }))
                    }
                  />
                  <span>
                    I understand that cut-to-order textile items cannot usually
                    be returned unless faulty.
                  </span>
                </label>
              ) : (
                <p className="checkout-hof__policy-note">
                  By placing your order you agree to the website{" "}
                  <Link to="/terms-and-conditions">terms and conditions</Link>{" "}
                  and the standard returns policy for eligible UK orders.
                </p>
              )}
            </div>

            <label className="checkout-hof__field">
              <span>Order note</span>
              <textarea
                rows={4}
                value={notes}
                maxLength={1000}
                onChange={(event) => setNotes(event.target.value)}
              />
            </label>

            {!canPay ? (
              <p className="checkout-hof__hint">
                Complete all required checkout details before payment.
              </p>
            ) : null}
          </CheckoutStep>

          <section className="checkout-hof__confirmation">
            <span />
            <h2>Order Confirmation</h2>
          </section>
        </div>

        <BagSummary
          cart={cart}
          quote={quote}
          fulfillmentMethod={fulfillmentMethod}
          currency={currency}
          loading={Boolean(pending.quote)}
        />
      </div>
    </main>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Wrapper                                  */
/* -------------------------------------------------------------------------- */

export function ShippingScreen() {
  return <ShippingScreenContent />;
}

export default function CheckoutScreen() {
  return <CheckoutScreenContent />;
}
